import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Paper
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  LocalShipping as DispatchIcon,
  CheckCircle as DeliverIcon,
  PictureAsPdf as PdfIcon,
  DirectionsCar as InTransitIcon
} from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { DocumentListPage, DocumentAction } from '../../components/documents/DocumentListPage';
import { deliveryChallanService } from '../../services/deliveryChallanService';
import { 
  DeliveryChallan, 
  DeliveryChallanStatus, 
  DeliveryChallanSearchFilters,
  DELIVERY_CHALLAN_STATUS_COLORS,
  DELIVERY_CHALLAN_STATUS_LABELS
} from '../../types/deliveryChallan';
import { formatCurrency } from '../../utils/formatters';

const DeliveryChallansPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [deliveryChallans, setDeliveryChallans] = useState<DeliveryChallan[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<DeliveryChallanSearchFilters>({});

  // Fetch delivery challans
  const fetchDeliveryChallans = useCallback(async () => {
    try {
      setLoading(true);
      const searchFilters: DeliveryChallanSearchFilters = {
        ...filters,
        search_term: searchTerm || undefined
      };
      
      const response = await deliveryChallanService.getDeliveryChallans(searchFilters);
      setDeliveryChallans(response.delivery_challans);
    } catch (error: any) {
      console.error('Failed to fetch delivery challans:', error);
      enqueueSnackbar('Failed to load delivery challans', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm, enqueueSnackbar]);

  useEffect(() => {
    fetchDeliveryChallans();
  }, [fetchDeliveryChallans]);

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
  };

  const handleCreateNew = () => {
    navigate('/admin/documents/delivery-challans/create');
  };

  const handleRowClick = (deliveryChallan: DeliveryChallan) => {
    navigate(`/admin/documents/delivery-challans/${deliveryChallan.id}`);
  };

  const handleEdit = (deliveryChallan: DeliveryChallan) => {
    navigate(`/admin/documents/delivery-challans/${deliveryChallan.id}/edit`);
  };

  const handleView = (deliveryChallan: DeliveryChallan) => {
    navigate(`/admin/documents/delivery-challans/${deliveryChallan.id}`);
  };

  const handleDownloadPDF = async (deliveryChallan: DeliveryChallan) => {
    try {
      const blob = await deliveryChallanService.downloadPDF(deliveryChallan.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${deliveryChallan.challan_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to download PDF:', error);
      enqueueSnackbar('Failed to download PDF', { variant: 'error' });
    }
  };

  const handleDispatch = async (deliveryChallan: DeliveryChallan) => {
    try {
      await deliveryChallanService.markAsDispatched(deliveryChallan.id);
      enqueueSnackbar('Delivery challan marked as dispatched', { variant: 'success' });
      fetchDeliveryChallans();
    } catch (error: any) {
      console.error('Failed to dispatch delivery challan:', error);
      enqueueSnackbar('Failed to dispatch delivery challan', { variant: 'error' });
    }
  };

  const handleDeliver = async (deliveryChallan: DeliveryChallan) => {
    try {
      await deliveryChallanService.markAsDelivered(deliveryChallan.id);
      enqueueSnackbar('Delivery challan marked as delivered', { variant: 'success' });
      fetchDeliveryChallans();
    } catch (error: any) {
      console.error('Failed to mark as delivered:', error);
      enqueueSnackbar('Failed to mark as delivered', { variant: 'error' });
    }
  };

  const handleMarkInTransit = async (deliveryChallan: DeliveryChallan) => {
    try {
      await deliveryChallanService.updateStatus(deliveryChallan.id, {
        status: DeliveryChallanStatus.IN_TRANSIT
      });
      enqueueSnackbar('Delivery challan marked as in transit', { variant: 'success' });
      fetchDeliveryChallans();
    } catch (error: any) {
      console.error('Failed to mark as in transit:', error);
      enqueueSnackbar('Failed to mark as in transit', { variant: 'error' });
    }
  };

  const getStatusColor = useCallback((status: DeliveryChallanStatus) => {
    return DELIVERY_CHALLAN_STATUS_COLORS[status] || 'default';
  }, []);

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'challan_number',
      headerName: 'Challan Number',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ fontWeight: 600, color: 'primary.main' }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'customer',
      headerName: 'Customer',
      width: 200,
      renderCell: (params) => (
        <Box>
          <Box sx={{ fontWeight: 500 }}>
            {params.row.customer?.business_name || 'N/A'}
          </Box>
          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {params.row.customer?.contact_person}
          </Box>
        </Box>
      )
    },
    {
      field: 'sales_order',
      headerName: 'Sales Order',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Box sx={{ fontWeight: 500 }}>
            {params.row.sales_order?.order_number || 'N/A'}
          </Box>
          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {params.row.sales_order?.total_amount ? formatCurrency(params.row.sales_order.total_amount) : ''}
          </Box>
        </Box>
      )
    },
    {
      field: 'challan_date',
      headerName: 'Challan Date',
      width: 120,
      renderCell: (params) => (
        format(new Date(params.value), 'dd/MM/yyyy')
      )
    },
    {
      field: 'dispatch_date',
      headerName: 'Dispatch Date',
      width: 120,
      renderCell: (params) => (
        params.value ? format(new Date(params.value), 'dd/MM/yyyy') : '-'
      )
    },
    {
      field: 'transporter_name',
      headerName: 'Transporter',
      width: 150,
      renderCell: (params) => params.value || '-'
    },
    {
      field: 'vehicle_number',
      headerName: 'Vehicle',
      width: 120,
      renderCell: (params) => params.value || '-'
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={DELIVERY_CHALLAN_STATUS_LABELS[params.value as DeliveryChallanStatus]}
          color={getStatusColor(params.value as DeliveryChallanStatus) as any}
          size="small"
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      renderCell: (params) => (
        format(new Date(params.value), 'dd/MM/yyyy')
      )
    }
  ], [getStatusColor]);

  const actions: DocumentAction[] = useMemo(() => [
    {
      icon: <ViewIcon />,
      label: 'View Details',
      onClick: handleView
    },
    {
      icon: <EditIcon />,
      label: 'Edit',
      onClick: handleEdit,
      condition: (item: DeliveryChallan) => item?.status === DeliveryChallanStatus.DRAFT
    },
    {
      icon: <PdfIcon />,
      label: 'Download PDF',
      onClick: handleDownloadPDF
    },
    {
      icon: <DispatchIcon />,
      label: 'Mark as Dispatched',
      onClick: handleDispatch,
      condition: (item: DeliveryChallan) => item?.status === DeliveryChallanStatus.DRAFT
    },
    {
      icon: <InTransitIcon />,
      label: 'Mark In Transit',
      onClick: handleMarkInTransit,
      condition: (item: DeliveryChallan) => item?.status === DeliveryChallanStatus.DISPATCHED
    },
    {
      icon: <DeliverIcon />,
      label: 'Mark as Delivered',
      onClick: handleDeliver,
      condition: (item: DeliveryChallan) => item && [
        DeliveryChallanStatus.DISPATCHED,
        DeliveryChallanStatus.IN_TRANSIT
      ].includes(item.status)
    }
  ], []);

  const handleStatusFilterChange = (status: DeliveryChallanStatus | '') => {
    setFilters(prev => ({
      ...prev,
      status: status || undefined
    }));
  };

  return (
    <Box>
      {/* Additional Filters */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                onChange={(e) => handleStatusFilterChange(e.target.value as DeliveryChallanStatus | '')}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {Object.values(DeliveryChallanStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {DELIVERY_CHALLAN_STATUS_LABELS[status]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Transporter"
              value={filters.transporter_name || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, transporter_name: e.target.value || undefined }))}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              value={filters.challan_date_from || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, challan_date_from: e.target.value || undefined }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              value={filters.challan_date_to || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, challan_date_to: e.target.value || undefined }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      <DocumentListPage
        title="Delivery Challans"
        documents={deliveryChallans}
        columns={columns}
        loading={loading}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onCreateNew={handleCreateNew}
        onRowClick={handleRowClick}
        actions={actions}
        getRowId={(row) => row.id}
      />
    </Box>
  );
};

export default DeliveryChallansPage;